<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin_verification.competitor_verification") }}</template>
        <template v-slot:body>
            <div class="columns is-mobile is-multiline">
                <div class="column">
                    <div class="is-size-5 has-text-weight-semibold clarity-mask">
                        {{ competitor.firstName }}
                        {{ competitor.lastName }}
                    </div>
                    <div class="has-text-grey-light">{{ competitor.academy }}</div>
                    <div class="has-text-white">{{ competitor.category }}</div>
                </div>
                <div class="column is-narrow has-text-right">
                    <VerificationStatus :verification-state="competitor.status.value" as-tag />
                    <div v-if="supportsAssigningPlaceToCompetitor" class="field mt-3" :class="{ 'has-error': !validSpotNumber }">
                        <label class="label">
                            {{ translate("admin_verification.assigned_place") }}
                        </label>
                        <div class="control">
                            <input
                                class="input spot-number has-text-right"
                                type="text"
                                v-model="eventRegistrationSpotNumber"
                                :disabled="competitor.status.value !== CANDIDATE"
                            />
                        </div>
                        <p v-if="!validSpotNumber" class="help">
                            {{ translate("admin_verification.allowed_values") }}
                        </p>
                    </div>
                </div>

                <div class="column is-12">
                    <FightEstimatedTime v-if="eta" :eta="eta" />
                    <div v-else-if="etaLoading" class="mt-2 has-text-grey">
                        <InlineSpinner />
                    </div>
                    <div v-else-if="etaFailed" class="mt-2 is-size-6 has-text-danger">
                        <span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
                        <span>{{ translate("admin_verification.fight_eta_failed") }}</span>
                        <a class="ml-2" @click="loadFightEstimatedTime">{{ translate("common.refresh") }}</a>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <template v-if="competitor.status.value === CANDIDATE">
                    <LoadingButton
                        class="button is-success"
                        @click="verify"
                        :is-loading="verificationInProgress"
                        :disabled="buttonsDisabledDueToRequest || !verificationLocksOpen"
                    >
                        <span class="icon">
                            <i class="fa fa-check" />
                        </span>
                        <span>
                            {{ translate("admin_verification.verify_action") }}
                        </span>
                    </LoadingButton>
                    <LoadingButton
                        class="button is-warning"
                        @click="disqualify"
                        :is-loading="dqInProgress"
                        :disabled="buttonsDisabledDueToRequest"
                    >
                        <span class="icon">
                            <i class="fa fa-times-circle" />
                        </span>
                        <span>
                            {{ translate("admin_verification.disqualify_action") }}
                        </span>
                    </LoadingButton>
                </template>
                <LoadingButton
                    v-else
                    class="button is-primary"
                    :disabled="!canUndo() || buttonsDisabledDueToRequest"
                    @click="undoVerification"
                    :is-loading="undoInProgress"
                >
                    <span class="icon">
                        <i class="fa fa-undo" />
                    </span>
                    <span>
                        {{ translate("admin_verification.undo_action") }}
                    </span>
                </LoadingButton>

                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import VerificationStatus from "./VerificationStatus.vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import InlineSpinner from "../Common/InlineSpinner.vue";
    import FightEstimatedTime from "./FightEstimatedTime.vue";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { CompetitorWithCategory } from "../Api/eventCompetitors";
    import verificationsApi, {
        AUTO_DQ,
        CANDIDATE,
        DISQUALIFIED_FAILED_VERIFICATION,
        VerificationTarget,
        VERIFIED,
    } from "../Api/verifications";
    import fightEstimatedTimeApi, { FightEstimatedTime as FightEstimatedTimeResponse } from "../Api/fightEstimatedTime";
    import { useVerificationStore } from "./verificationStore";
    import { Status } from "../Common/Competitors/types";

    const props = defineProps<{
        eventTypes: string[];
    }>();

    const emit = defineEmits<{
        finished: [];
    }>();

    const store = useVerificationStore();

    const { open: openModal, close, modal } = useModal();

    const verificationInProgress = ref(false);
    const dqInProgress = ref(false);
    const undoInProgress = ref(false);
    const eventRegistrationSpotNumber = ref<number | null>(null);

    let latestEtaRequest = 0;

    const eta = ref<FightEstimatedTimeResponse | null>(null);
    const etaLoading = ref(false);
    const etaFailed = ref(false);

    const competitor = ref<CompetitorWithCategory>({
        id: 0,
        firstName: "",
        lastName: "",
        academy: "",
        branch: "",
        category: "",
        status: {
            value: CANDIDATE,
        } as Status,
        eventRegistrationSpotNumber: null,
    });

    const validSpotNumber = computed(() => {
        if (!eventRegistrationSpotNumber.value) {
            return true;
        }

        return eventRegistrationSpotNumber.value >= 0 && eventRegistrationSpotNumber.value <= 1000;
    });

    const buttonsDisabledDueToRequest = computed(() => {
        return verificationInProgress.value || dqInProgress.value || undoInProgress.value || !validSpotNumber.value;
    });

    // Verify stays disabled until the backend verdict arrives, because desk Staff cannot know the locks up front.
    const verificationLocksOpen = computed(() => eta.value !== null && eta.value.earlyLockOpen && eta.value.queueLockOpen);

    const supportsAssigningPlaceToCompetitor = computed(() => {
        return props.eventTypes.includes("Wrestling");
    });

    function open(competitorData: CompetitorWithCategory) {
        eventRegistrationSpotNumber.value = competitorData.eventRegistrationSpotNumber;
        competitor.value = competitorData;
        openModal();
        loadFightEstimatedTime();
    }

    function loadFightEstimatedTime() {
        const request = ++latestEtaRequest;
        eta.value = null;
        etaFailed.value = false;

        // Locks only gate verifying; the undo view (any non-pending status) has nothing to show.
        if (competitor.value.status.value !== CANDIDATE) {
            etaLoading.value = false;
            return Promise.resolve();
        }

        etaLoading.value = true;

        return fightEstimatedTimeApi
            .getFightTimeForEventCompetitor(store.eventId, competitor.value.id)
            .then(fightEta => {
                if (request === latestEtaRequest) {
                    eta.value = fightEta;
                }
            })
            .catch(error => {
                if (request === latestEtaRequest) {
                    etaFailed.value = true;
                }
                handleError(error);
            })
            .finally(() => {
                if (request === latestEtaRequest) {
                    etaLoading.value = false;
                }
            });
    }

    function verify() {
        verificationInProgress.value = true;
        return sendVerificationRequest(VerificationTarget.Verified)
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => (verificationInProgress.value = false));
    }

    function disqualify() {
        dqInProgress.value = true;
        return sendVerificationRequest(VerificationTarget.DisqualifiedFailedVerification)
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => (dqInProgress.value = false));
    }

    function sendVerificationRequest(target: VerificationTarget) {
        return Promise.resolve()
            .then(() => {
                if (!competitor.value.id) {
                    throw Error("No selected competitor.");
                }

                return verificationsApi.sendVerificationRequest(store.eventId, {
                    selectedCompetitorId: competitor.value.id,
                    target: target,
                    eventRegistrationSpotNumber: eventRegistrationSpotNumber.value || null,
                });
            })
            .then(() => {
                emit("finished");
            });
    }

    function undoVerification() {
        undoInProgress.value = true;
        return Promise.resolve()
            .then(() => {
                if (!competitor.value.id) {
                    throw Error("No selected competitor.");
                }
                return verificationsApi.undoVerification(store.eventId, competitor.value.id);
            })
            .then(() => {
                close();
                emit("finished");
            })
            .catch(handleError)
            .finally(() => (undoInProgress.value = false));
    }

    function canUndo() {
        return (
            competitor.value.status.value === VERIFIED ||
            competitor.value.status.value === DISQUALIFIED_FAILED_VERIFICATION ||
            competitor.value.status.value === AUTO_DQ
        );
    }

    defineExpose({
        open,
    });
</script>

<style scoped>
    .spot-number {
        width: 8rem;
    }
</style>
